import React, { useState } from 'react';
import classnames from 'classnames';
import {
  Modal as ModalContainer,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { Button } from 'components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import PropTypes from 'prop-types';

import './Modal.scss';

/** A wrapper for the reactstrap modal component ensuring more consistent styling */
const Modal = props => {
  const [showModal, setShowModal] = useState(false);

  const {
    buttonContents,
    buttonProps,
    children,
    className,
    color,
    footer,
    hideCloseButton,
    onClosed,
    onEnter,
    onExit,
    onOpened,
    size,
    show,
    title,
    toggle,
  } = props;

  const onToggle = () => {
    if (show) {
      toggle();
    } else {
      setShowModal(!showModal);
    }
    document.activeElement.blur();
  };

  return (
    <React.Fragment>
      {buttonContents && (
        <Button {...buttonProps} onClick={onToggle}>
          {buttonContents}
        </Button>
      )}
      <ModalContainer
        className={classnames(className, color)}
        isOpen={show || showModal}
        size={size}
        toggle={onToggle}
        onClosed={onClosed}
        onEnter={onEnter}
        onExit={onExit}
        onOpened={onOpened}
      >
        {!hideCloseButton && (
          <Button
            className="close-btn rounded-pill"
            color="light"
            onClick={onToggle}
            type="button"
            icon={<FontAwesomeIcon icon={faTimes} color="#444647" />}
          />
        )}
        {title && <ModalHeader tag="div">{title}</ModalHeader>}
        {children && <ModalBody>{children}</ModalBody>}
        {footer && <ModalFooter>{footer}</ModalFooter>}
      </ModalContainer>
    </React.Fragment>
  );
};

Modal.propTypes = {
  /** Contents of the trigger button */
  buttonContents: PropTypes.string,
  /** Props object for the trigger button */
  buttonProps: PropTypes.shape({}),
  /** Modal contents */
  children: PropTypes.node,
  /** Modal container class */
  className: PropTypes.string,
  /** Border color for confirmation modal */
  color: PropTypes.oneOf([
    'primary',
    'secondary',
    'success',
    'danger',
    'warning',
    'info',
    'light',
    'dark',
    null,
  ]),
  /** Footer contents and actions */
  footer: PropTypes.node,
  /** Show close button? */
  hideCloseButton: PropTypes.bool,
  /** Custom onClosed function */
  onClosed: PropTypes.func,
  /** Custom onEnter function */
  onEnter: PropTypes.func,
  /** Custom onExit function */
  onExit: PropTypes.func,
  /** Custom onOpened function */
  onOpened: PropTypes.func,
  /** Modal size */
  size: PropTypes.oneOf([null, 'auto', 'sm', 'lg']),
  /** Show or hide the modal */
  show: PropTypes.bool,
  /** Header node, will be in a `<h5 />` tag so avoid using tags apart from <span /> */
  title: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  /** Custom toggle function */
  toggle: PropTypes.func,
};

Modal.defaultProps = {
  buttonContents: null,
  buttonProps: null,
  children: null,
  className: null,
  color: null,
  footer: null,
  hideCloseButton: false,
  onClosed: () => {},
  onEnter: () => {},
  onExit: () => {},
  onOpened: () => {},
  size: null,
  show: null,
  title: null,
  toggle: () => {},
};

export default Modal;
